import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ThumbsDown, Flag, Plus, ExternalLink, Check, EyeOff, RefreshCw } from "lucide-react";

const STATUS_FILTERS = [
  { label: "New", value: "new" },
  { label: "Reviewed", value: "reviewed" },
  { label: "Acted on", value: "acted_on" },
  { label: "Dismissed", value: "dismissed" },
  { label: "All", value: "" },
];

const VOTE_META = {
  disagree: { label: "Disagree", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300", icon: <ThumbsDown className="h-3 w-3" /> },
  incorrect: { label: "Incorrect info", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300", icon: <Flag className="h-3 w-3" /> },
  add_evidence: { label: "Added evidence", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", icon: <Plus className="h-3 w-3" /> },
  agree: { label: "Agrees", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300", icon: <Check className="h-3 w-3" /> },
};

const FeedbackQueueTab = () => {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("new");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/feedback-queue", { params: { status: status || undefined } });
      setRows(data);
    } catch { toast.error("Failed to load feedback"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [status]);

  const setStatusFor = async (r, newStatus) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/feedback-queue/${r.id}/status`, { status: newStatus });
      toast.success(`Marked ${newStatus.replace("_", " ")}`);
      fetchRows();
    } catch { toast.error("Update failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5" data-testid="feedback-queue-tab">
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatus(s.value)}
            className={`text-xs px-3 py-1.5 rounded-full ${status === s.value ? "bg-primary text-primary-foreground" : "bg-muted"}`}
            data-testid={`fb-filter-${s.value || "all"}`}
          >{s.label}</button>
        ))}
        <button onClick={fetchRows} className="ml-auto text-xs px-3 py-1.5 rounded-full bg-muted flex items-center gap-1" data-testid="fb-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="card-premium overflow-hidden">
        {rows.length === 0 && (
          <div className="py-14 text-center text-sm text-muted-foreground">No {status || "feedback"} entries.</div>
        )}
        {rows.map((r) => {
          const vote = VOTE_META[r.vote] || { label: r.vote, cls: "bg-muted", icon: null };
          return (
            <div key={r.id} className="grid grid-cols-12 gap-3 px-5 py-4 items-start border-b border-border/40 last:border-b-0 hover:bg-muted/20" data-testid={`fb-row-${r.id}`}>
              <div className="col-span-5 min-w-0">
                <div className="font-semibold truncate text-sm">{r.product_name || r.product_id}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.product_brand} · score {r.current_score ?? r.score_at_vote}</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-2 py-0.5 ${vote.cls}`}>
                    {vote.icon}{vote.label}
                  </span>
                  {r.dimension && (
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">· {r.dimension}</span>
                  )}
                </div>
              </div>
              <div className="col-span-4 text-xs text-muted-foreground min-w-0">
                {r.note && <p className="line-clamp-3">{r.note}</p>}
                {r.evidence_url && (
                  <a href={r.evidence_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline mt-1">
                    <ExternalLink className="h-3 w-3" /> Evidence link
                  </a>
                )}
                {r.user_id && <div className="mt-1 text-[10px]">User {r.user_id.slice(0, 8)}</div>}
              </div>
              <div className="col-span-3 flex flex-col items-end gap-1">
                <div className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleString()}</div>
                {r.status === "new" ? (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setStatusFor(r, "reviewed")} disabled={busyId === r.id} className="text-[10px] px-2 py-1 rounded-full bg-muted hover:bg-emerald-100 dark:hover:bg-emerald-900/40 flex items-center gap-1 disabled:opacity-50" data-testid={`fb-review-${r.id}`}><Check className="h-3 w-3" /> Reviewed</button>
                    <button onClick={() => setStatusFor(r, "acted_on")} disabled={busyId === r.id} className="text-[10px] px-2 py-1 rounded-full bg-emerald-600 text-white flex items-center gap-1 disabled:opacity-50" data-testid={`fb-act-${r.id}`}>Act on it</button>
                    <button onClick={() => setStatusFor(r, "dismissed")} disabled={busyId === r.id} className="text-[10px] px-2 py-1 rounded-full bg-muted hover:bg-muted/70 flex items-center gap-1 disabled:opacity-50" data-testid={`fb-dismiss-${r.id}`}><EyeOff className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{r.status}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FeedbackQueueTab;
